import { AuthenticationService } from '@stockcare-services/authentication/authentication.service';
import { MatDialog } from '@angular/material/dialog';
import { CreatePinComponent } from '../create-pin/create-pin.component';
import { DeviceService } from '@stockcare-services/devices-service/devices.service';
import { IDevice } from '@stockcare-models/IDevice';
import { ActivityService } from '@stockcare-services/activity-service/activity.service';
import { ApplicationInsightsService } from '@stockcare-services/application-insights/application-insights.service';
import { Roles } from '@stockcare-helpers/roles';
import { SettingsService } from '@stockcare-services/settings-service/settings.service';
import { Settings } from '@stockcare-helpers/settings';
import { KeyboardTemplate } from '@stockcare-components/custom-keyboard/keyboard-templates';
import { CustomKeyboardComponent } from '@stockcare-components/custom-keyboard/custom-keyboard.component';
import { Component, ElementRef, EventEmitter, OnInit, Output, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';

@Component({
  standalone: false,
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
})
export class LoginComponent implements OnInit {
  @ViewChild('appKeyboard') public appKeyboard: CustomKeyboardComponent;
  @ViewChild('usernameKeyboard') public usernameKeyboard: CustomKeyboardComponent;
  @ViewChild('passwordKeyboard') public passwordKeyboard: CustomKeyboardComponent;
  @ViewChild('userNameInput', { static: false }) userNameInput: ElementRef;
  @ViewChild('passwordInput', { static: false }) passwordInput: ElementRef;

  isLinked: boolean;
  isAdministrator: boolean;
  closeResult: string;
  loginForm: UntypedFormGroup;
  alert: string;
  dismissible: boolean = true;
  loginError: boolean = false;
  inputtype: string = 'password';
  isDeviceDeactivated: boolean = false;
  enableCustomKeyboard: boolean = false;
  keyboardInputMode: string = 'text';
  keyboardTemplate = KeyboardTemplate.StandardWithSpaceAndEnter;
  inputName = 'username';
  isTrainingRequested: boolean = false;
  isTrainingRequestsEnabled: boolean = false;
  inputs = {
    userName: '',
    password: '',
  };
  placeholderUsername: string = 'Username';
  placeholderPassword: string = 'Password';
  kronosLogin: boolean = false;
  public showUsernameKeyboard: boolean = false;
  public showPasswordKeyboard: boolean = false;

  submitted: boolean = false;
  @Output() createPin: EventEmitter<any> = new EventEmitter();
  device: IDevice;

  constructor(
    private router: Router,
    private fb: UntypedFormBuilder,
    private authService: AuthenticationService,
    private modalService: MatDialog,
    private deviceService: DeviceService,
    private activityService: ActivityService,
    private applicationInsightsService: ApplicationInsightsService,
    private settingsService: SettingsService
  ) { }

  ngOnInit() {
    if (!this.authService.isActionCenterLogin) {
      this.authService.logout();
    }

    this.kronosLogin = this.settingsService.getKronosLoginSetting();
    this.setKronosLogin();

    this.loginForm = this.fb.group({
      username: [null, [Validators.required]],
      password: [null, [Validators.required]],
    });

    this.enableCustomKeyboard = this.settingsService.enableCustomKeyboard();
    this.isTrainingRequestsEnabled = this.settingsService.getBooleanSetting(Settings.ENABLE_REQUEST_TRAINING);

    this.device = this.deviceService.getDeviceFromLocalStorage();
    this.isLinked = this.deviceService.isDeviceLinked();

    this.setDefaultSelection();
  }

  get controls() {
    return this.loginForm.controls;
  }

  get getInputMode() {
    if (this.enableCustomKeyboard) {
      return 'none';
    }
    return 'text';
  }

  async loginAndNavigateToRequestTraining() {
    this.isTrainingRequested = true;
    this.login();
  }

  onInputFocus = (event: any) => {
    this.inputName = event.target.id;
    if (this.enableCustomKeyboard) {
      switch (this.inputName) {
        case 'username':
          this.showUsernameKeyboard = true;
          this.showPasswordKeyboard = false;
          break;
        case 'password':
          this.showUsernameKeyboard = false;
          this.showPasswordKeyboard = true;
          break;
        default:
          this.showUsernameKeyboard = false;
          this.showPasswordKeyboard = false;
          break;
      }
    } else {
      this.appKeyboard?.keyboard?.setOptions({
        inputName: event.target.id,
      });
    }


  };

  scroll(el: HTMLElement) {
    el.scrollTo();
  }

  setDefaultSelection() {
    if (this.enableCustomKeyboard) {
      document.getElementById('username').focus();
    }
  }

  setKronosLogin() {
    if (this.kronosLogin) {
      this.placeholderUsername = 'Kronos ID';
      this.placeholderPassword = 'Kronos Pin';
    }
  }

  public inputText(inputText: string): void {
    this.loginForm.get(this.inputName).setValue(inputText);

    let caretPosition = this.appKeyboard.keyboard.caretPosition;

    if (caretPosition !== null) this.setInputCaretPosition(document.querySelector(`#${this.inputName}`), caretPosition);
  }

  onInputChange = (event: any) => {
    if (this.enableCustomKeyboard) {
      this.appKeyboard.keyboard.setInput(event.target.value, event.target.id);
    }
  };

  setInputCaretPosition = (element: any, position: number) => {
    if (element.setSelectionRange) {
      element.focus();
      element.setSelectionRange(position, position);
    }
  };

  public usernameKeyboardInput(username: string): void {
    this.controls.username.setValue(username);
  }
  public passwordKeyboardInput(password: string): void {
    this.controls.password.setValue(password);
  }

  login() {
    if (this.kronosLogin) {
      this.loginForm.value.password = this.loginForm.value.password.replace(/\s/g, "");
      this.loginForm.value.username = this.loginForm.value.username.replace(/\s/g, "");
    }
    
    this.submitted = true;
    if (this.loginForm.valid) {
      const loginBody = this.loginForm.value;
      loginBody.deviceId = this.deviceService.getDeviceId();

      this.authService.login(loginBody).subscribe(
        (isLoggedIn) => {
          if (isLoggedIn) {
            this.applicationInsightsService.logEvent('logged-in-with-username', { success: true, deviceId: loginBody.deviceId });

            if (!this.kronosLogin) {
              this.openSetPinDialog();
            }
            else {
              this.onLoggedIn()
            }

          }
        },
        (err) => {
          this.applicationInsightsService.logEvent('logged-in-with-username', {
            success: false,
            deviceId: loginBody.deviceId,
          });
          this.loginError = true;
          this.alert = err.error;
        }
      );
    }
  }

  private onLoggedIn() {
    if (this.authService.isActionCenterLogin) {
      this.navigateToActionCenter();
      return;
    }
    const isLinked = this.deviceService.isDeviceLinked();
    const isAdministrator = this.authService.isAdministrator();

    if (!isLinked) {
      this.linkDevice(isAdministrator);
      return;
    }

    if (this.deviceService.isDeviceRemoved()) {
      this.activateDevice();
      return;
    }

    this.activityService.start();

    if (this.isTrainingRequested) {
      this.navigateToRequestTraining();
      return;
    }

    if (this.deviceService.isWhiteboardDevice()) {
      this.navigateToWhiteBoard();
      return;
    }

    if (this.deviceService.isTheaterDevice()) {
      this.navigateToTheatre();
      return;
    }

    if (this.deviceService.isEmergencyCupboardDevice()) {
      this.navigateToEmergencyCupboard();
      return;
    }

    var canViewPatients = this.authService.isInRole([
      Roles.systemAdministrator,
      Roles.superAdministrator,
      Roles.stockIssuer,
      Roles.creditRequester,
    ]);

    if (canViewPatients) {
      this.router.navigate(['app/patients']);
      return;
    }

    var canApproveCredits = this.authService.isInRole([Roles.systemAdministrator, Roles.superAdministrator, Roles.creditApprover]);

    if (canApproveCredits) {
      this.router.navigate(['app/approvals']);
      return;
    }

    this.router.navigate(['app/baskets']);
    return;
  }

  private navigateToActionCenter() {
    this.router.navigate(['/app/action-center']);
  }

  private navigateToWhiteBoard() {
    this.router.navigate(['app/whiteboard']);
  }

  private navigateToTheatre() {
    this.router.navigate(['app/theatre']);
  }

  private navigateToRequestTraining() {
    this.router.navigate(['/app/request-training/new-request']);
  }

  private navigateToEmergencyCupboard() {
    this.router.navigate(['app/emergency-cupboard']);
  }

  private linkDevice(isAdministrator: boolean) {
    this.router.navigate([isAdministrator ? 'app/authentication/linkdevice' : 'app/authentication/deviceneedstobelinked']);
  }

  private activateDevice() {
    this.isDeviceDeactivated = true;
    this.loginError = true;
    this.alert = 'This device has been deactivated. Contact your administrator.';
    this.router.navigate(['app/authentication/devicedeactivated']);
  }

  onClosed() {
    this.loginError = false;
  }

  private openSetPinDialog() {
    if (this.enableCustomKeyboard) {
      return this.createPin.emit(this.loginForm.get('username').value.toLowerCase());
    }
    this.modalService
      .open(CreatePinComponent, {
        data: this.loginForm.get('username').value.toLowerCase(),
        width: '300px',
        disableClose: true,
      })
      .afterClosed()
      .subscribe((x) => {
        if (x) {
          this.onLoggedIn();
        }
      });
  }

  showPassword($event) {
    $event.preventDefault();
    if (this.inputtype === 'password') {
      this.inputtype = 'text';
    } else {
      this.inputtype = 'password';
    }
  }
}
